import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CalendarClock,
  CalendarDays,
  ChartNoAxesCombined,
  ChartPie,
  FileText,
  FileWarning,
  GitCompareArrows,
  GraduationCap,
  HandCoins,
  Handshake,
  Landmark,
  MessageSquare,
  PackageMinus,
  Plug,
  Receipt,
  Scale,
  TriangleAlert,
  Trophy,
  Truck,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';

/** Fase 2 (W1): formato de `GET /inicio/indicadores` (contrato em `packages/kernel/src/registro.ts`). */
export type Natureza = 'fluxo' | 'posicao' | 'fila';
export type Secao = 'financeiro' | 'comercial' | 'operacao' | 'pessoas' | 'sistema';
export const SECOES: Secao[] = ['financeiro', 'comercial', 'operacao', 'pessoas', 'sistema'];

export interface ValorPorMoeda {
  moeda: string;
  valor: number;
  anterior?: number | null;
}

export interface ParteIndicador {
  chave: string;
  valor: number;
  por_moeda?: ValorPorMoeda[];
  formato?: 'numero' | 'dinheiro' | 'percentual';
  tom?: 'perigo' | 'atencao';
  link?: string;
}

export interface ItemIndicador {
  id: string;
  titulo: string;
  tipo_titulo?: 'pessoa' | 'texto' | 'codigo';
  rotulo?: string;
  data?: string;
  valor?: number;
  moeda?: string;
  dias?: number;
  autor?: string;
  autor_tipo?: string;
  link?: string;
}

export interface PontoSerie {
  data: string;
  entradas: number;
  saidas: number;
  saldo: number;
  /** Fase 2 (W21, K7): saldo previsto (com os títulos em aberto por vencimento), de hoje em diante. */
  previsto?: number | null;
}

export interface IndicadorApi {
  chave: string;
  modulo: string;
  rotulo: string;
  formato: 'numero' | 'dinheiro' | 'texto';
  link: string | null;
  icone: string | null;
  natureza: Natureza;
  secao: Secao;
  base: 'caixa' | 'competencia' | 'emissao' | null;
  comparar: 'subir_bom' | 'subir_ruim' | null;
  apresentacao: 'valor' | 'grafico' | 'lista';
  ordem: number;
  largura: 1 | 2;
  periodo: { de: string; ate: string } | null;
  periodo_anterior: { de: string; ate: string } | null;
  estado: 'ok' | 'indisponivel';
  falha?: 'erro' | 'tempo';
  valor: number | string | null;
  anterior?: number | null;
  por_moeda?: ValorPorMoeda[] | null;
  subtitulo?: string | null;
  partes?: ParteIndicador[] | null;
  serie?: { moeda: string; granularidade: 'dia' | 'semana'; pontos: PontoSerie[] } | null;
  lista?: ItemIndicador[] | null;
}

/** Ícone lucide pelo nome que o módulo declara; desconhecido usa o raio genérico. */
const ICONES: Record<string, LucideIcon> = {
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CalendarClock,
  CalendarDays,
  ChartNoAxesCombined,
  ChartPie,
  FileText,
  FileWarning,
  GitCompareArrows,
  GraduationCap,
  HandCoins,
  Handshake,
  Landmark,
  MessageSquare,
  PackageMinus,
  Plug,
  Receipt,
  Scale,
  TriangleAlert,
  Trophy,
  Truck,
  UserCheck,
  Users,
};
export const iconeDoIndicador = (nome: string | null): LucideIcon => (nome && ICONES[nome]) || Zap;

/** Variação percentual contra o anterior; null quando não há base (anterior zero ou ausente). */
export function variacao(valor: number, anterior: number | null | undefined): number | null {
  if (anterior === null || anterior === undefined || anterior === 0) return null;
  return ((valor - anterior) / Math.abs(anterior)) * 100;
}

/** Seções com algum card, na ordem fixa, e os cards de cada uma pela ordem declarada. */
export function agruparPorSecao(itens: IndicadorApi[]): { secao: Secao; itens: IndicadorApi[] }[] {
  return SECOES.map((secao) => ({ secao, itens: itens.filter((i) => i.secao === secao).sort((a, b) => a.ordem - b.ordem || a.chave.localeCompare(b.chave)) })).filter((g) => g.itens.length > 0);
}
